import { readFile } from "node:fs/promises";
import { homedir } from "node:os";
import { isAbsolute, join } from "node:path";
const SESSION_ERROR_STALE_WINDOW_MS = 30_000;
export async function readRecentOpenClawSessionErrorMessage(input) {
    const sessionIndex = await readSessionIndex({
        agentId: input.agentId,
        env: input.env,
    });
    if (!sessionIndex) {
        return null;
    }
    const sessionEntry = sessionIndex[input.sessionKey];
    if (!sessionEntry) {
        return null;
    }
    const updatedAtMs = normalizeOptionalNumber(sessionEntry.updatedAt);
    if (updatedAtMs !== null &&
        updatedAtMs < input.minTimestampMs - SESSION_ERROR_STALE_WINDOW_MS) {
        return null;
    }
    const sessionFilePath = resolveSessionFilePath({
        agentId: input.agentId,
        env: input.env,
        sessionEntry,
    });
    if (!sessionFilePath) {
        return null;
    }
    let sessionDocument;
    try {
        sessionDocument = await readFile(sessionFilePath, "utf8");
    }
    catch {
        return null;
    }
    return extractRecentSessionErrorMessage({
        minTimestampMs: input.minTimestampMs,
        sessionDocument,
    });
}
async function readSessionIndex(input) {
    const sessionIndexPath = join(resolveOpenClawStateDir(input.env), "agents", input.agentId, "sessions", "sessions.json");
    let document;
    try {
        document = await readFile(sessionIndexPath, "utf8");
    }
    catch {
        return null;
    }
    try {
        const parsed = JSON.parse(document);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
            return null;
        }
        return parsed;
    }
    catch {
        return null;
    }
}
function resolveOpenClawStateDir(env) {
    const configuredStateDir = normalizeOptionalText(env?.OPENCLAW_STATE_DIR);
    if (configuredStateDir) {
        return configuredStateDir;
    }
    return join(homedir(), ".openclaw");
}
function resolveSessionFilePath(input) {
    const sessionsDir = join(resolveOpenClawStateDir(input.env), "agents", input.agentId, "sessions");
    const sessionFile = normalizeOptionalText(input.sessionEntry.sessionFile);
    if (sessionFile) {
        return isAbsolute(sessionFile)
            ? sessionFile
            : join(sessionsDir, sessionFile);
    }
    const sessionId = normalizeOptionalText(input.sessionEntry.sessionId);
    if (!sessionId) {
        return null;
    }
    return join(sessionsDir, `${sessionId}.jsonl`);
}
function extractRecentSessionErrorMessage(input) {
    const lines = input.sessionDocument
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
    for (let index = lines.length - 1; index >= 0; index -= 1) {
        const line = lines[index];
        if (!line) {
            continue;
        }
        const errorRecord = parseSessionErrorRecord(line);
        if (!errorRecord) {
            continue;
        }
        if (errorRecord.timestampMs <
            input.minTimestampMs - SESSION_ERROR_STALE_WINDOW_MS) {
            return null;
        }
        return errorRecord.errorMessage;
    }
    return null;
}
function parseSessionErrorRecord(line) {
    try {
        const parsed = JSON.parse(line);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
            return null;
        }
        const record = parsed;
        if (normalizeOptionalText(record.type) !== "message") {
            return null;
        }
        const message = record.message && typeof record.message === "object"
            ? record.message
            : null;
        if (!message) {
            return null;
        }
        if (normalizeOptionalText(message.stopReason) !== "error") {
            return null;
        }
        const errorMessage = normalizeOptionalText(message.errorMessage);
        if (!errorMessage) {
            return null;
        }
        const messageTimestampMs = normalizeOptionalNumber(message.timestamp) ??
            normalizeIsoTimestampMs(record.timestamp);
        if (messageTimestampMs === null) {
            return null;
        }
        return {
            errorMessage,
            timestampMs: messageTimestampMs,
        };
    }
    catch {
        return null;
    }
}
function normalizeIsoTimestampMs(value) {
    const normalized = normalizeOptionalText(value);
    if (!normalized) {
        return null;
    }
    const parsed = Date.parse(normalized);
    return Number.isFinite(parsed) ? parsed : null;
}
function normalizeOptionalNumber(value) {
    return typeof value === "number" && Number.isFinite(value) ? value : null;
}
function normalizeOptionalText(value) {
    const normalized = typeof value === "string" ? value.trim() : "";
    return normalized.length > 0 ? normalized : null;
}
